<template>
  <div class="section">
    <div class="header">
      <label class="title">Proof of Play Metrics</label>
      <div class="controls">
        <select v-model="timeRange" class="time-select">
          <option value="1min">Previous Minute</option>
          <option value="1h">Previous Hour</option>
          <option value="12h">Previous 12 Hours</option>
          <option value="24h">Previous 24 Hours</option>
        </select>
        <button
          class="export-btn"
          :disabled="!proofOfPlayList.length"
          title="Export to Excel"
          @click="exportToExcel"
        >
          <ExportIcon class="export-icon" />
        </button>
      </div>
    </div>
    <div v-if="isLoading" class="loading">
      <span class="inline-spinner" /> Loading...
    </div>
    <div v-else-if="error" class="error">{{ error }}</div>
    <div v-else-if="proofOfPlayList.length" class="table-wrap">
      <table class="table">
        <thead>
          <tr>
            <th class="th th-media">Media</th>
            <th class="th th-num">Plays</th>
            <th v-if="hasAnySkips" class="th th-num">Skips</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item in proofOfPlayList"
            :key="item.media_id"
            class="row"
          >
            <td class="td td-media">
              <div class="media-cell">
                <MediaThumb
                  :src="thumbMap[item.media_name]"
                  :alt="item.media_name"
                  :size="72"
                />
                <span class="media-name" :title="item.media_name">{{ item.media_name }}</span>
              </div>
            </td>
            <td class="td td-num">{{ item.total_plays }}</td>
            <td v-if="hasAnySkips" class="td td-num">{{ item.total_skips }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-else class="empty">No playback data available</div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch } from 'vue';
import {
  getProofOfPlayMetrics,
  downloadProofOfPlayExport,
  type AlreadyPlayedContent,
  type ProofOfPlayTimeRangePreset,
} from '@/helpers/api/metrics';
import ExportIcon from '@/components/icons/ExportIcon.vue';
import MediaThumb from '@/components/MediaThumb.vue';
import useAssetThumbnails from '@/composables/useAssetThumbnails';

import useNotifications from '@/components/Notifications/composables/notifications';

const props = defineProps<{
  deviceUuid: string | null;
}>();

const { thumbMap, resolveThumbnails, retainThumbnails } = useAssetThumbnails();

const timeRange = ref<ProofOfPlayTimeRangePreset>('1min');
const proofOfPlayData = ref<AlreadyPlayedContent[]>([]);
const isLoading = ref(false);
const error = ref('');
// Monotonic request id — used to ignore responses from stale fetches
// (e.g. poll timer + filter change firing concurrently, or rapid filter
// changes). Only the latest request is allowed to mutate state.
let requestId = 0;

const proofOfPlayList = computed<AlreadyPlayedContent[]>(() =>
  proofOfPlayData.value.filter(
    (item) => item.total_plays > 0 || item.total_skips > 0
  )
);

const hasAnySkips = computed(() =>
  proofOfPlayList.value.some((item) => item.total_skips > 0)
);

async function fetchProofOfPlay(opts: { showSpinner?: boolean } = {}) {
  if (!props.deviceUuid) return;
  const myId = ++requestId;
  if (opts.showSpinner) {
    isLoading.value = true;
    error.value = '';
  }
  try {
    const resp = await getProofOfPlayMetrics({
      timeRange: timeRange.value,
      playerUuids: props.deviceUuid,
    });
    if (myId !== requestId) return; // stale response, drop it
    proofOfPlayData.value = resp.byMedia || [];
    const items = proofOfPlayData.value.map((item) => ({
      name: item.media_name,
      url: item.media_url,
    }));
    if (items.length > 0) {
      await resolveThumbnails(items);
      if (myId !== requestId) return; // filters changed while resolving
    }
    // Prune cache to current media so memory stays bounded
    retainThumbnails(items.map((i) => i.name));
    error.value = '';
  } catch (e: any) {
    if (myId !== requestId) return;
    error.value = e?.message || 'Failed to load proof of play data';
    console.error('[ProofOfPlayTable] fetch error:', e);
  } finally {
    if (myId === requestId) {
      isLoading.value = false;
    }
  }
}

const notification = useNotifications();

async function exportToExcel() {
  if (!proofOfPlayList.value.length || !props.deviceUuid) return;
  try {
    // Backend streams the XLSX. The byMedia sheet here will always have a
    // Player Count column of 1 since the filter pins a single device — accepted
    // as cosmetic noise rather than a per-player schema variant.
    await downloadProofOfPlayExport({
      timeRange: timeRange.value,
      playerUuids: props.deviceUuid,
      sections: 'byMedia',
    });
    notification.success('Exported proof of play data to Excel.');
  } catch (e: any) {
    notification.failure('Failed to export proof of play data.');
    console.error('[ProofOfPlayTable] exportToExcel error:', e);
  }
}

// Background polling removed — parent (PlayerMetrics) owns a refresh button
// that drives both this panel and DeviceMetrics. We just fetch on mount and
// whenever the user changes the time-range select or the deviceUuid prop.
watch(
  [timeRange, () => props.deviceUuid],
  () => fetchProofOfPlay({ showSpinner: true }),
  { immediate: true },
);

defineExpose({
  refresh: () => fetchProofOfPlay({ showSpinner: true }),
});
</script>

<style scoped>
.section {
  margin-top: 1.25rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e5e7eb;
}

.title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.time-select {
  appearance: none;
  -webkit-appearance: none;
  background: #f9fafb;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 0.35rem 2rem 0.35rem 0.75rem;
  font-size: 0.85rem;
  color: #374151;
  cursor: pointer;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23374151' d='M3 4.5L6 8l3-3.5H3z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.5rem center;
  transition: border-color 0.15s;
}

.time-select:hover { border-color: #9ca3af; }
.time-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
}

.export-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #f9fafb;
  cursor: pointer;
  transition: border-color 0.15s, background-color 0.15s;
}

.export-btn:hover { border-color: #9ca3af; background: #f3f4f6; }
.export-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.export-icon {
  width: 16px;
  height: 16px;
  color: #374151;
}

.table-wrap {
  max-height: 380px;
  overflow-y: auto;
}

.table-wrap::-webkit-scrollbar { width: 6px; }
.table-wrap::-webkit-scrollbar-track { background: transparent; }
.table-wrap::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 3px; }
.table-wrap::-webkit-scrollbar-thumb:hover { background: #9ca3af; }

.table {
  width: 100%;
  border-collapse: collapse;
  /* Fixed layout so long media names get ellipsized by .media-name's
     overflow rules instead of widening the table (and its parent column). */
  table-layout: fixed;
}

.th {
  position: sticky;
  top: 0;
  background: #f9fafb;
  padding: 0.5rem 1rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
}

.th-media { text-align: center; }
.th-num { text-align: center; width: 80px; }

.row { transition: background-color 0.15s; }
.row:hover { background: #f9fafb; }
.row + .row .td { border-top: 1px solid #f3f4f6; }

.td {
  padding: 0.625rem 1rem;
  vertical-align: middle;
}

.td-media {
  overflow: hidden;
}

.td-num {
  text-align: center;
  font-size: 0.95rem;
  font-weight: 600;
  color: #374151;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.media-cell {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
}

.media-name {
  font-size: 0.875rem;
  font-weight: 500;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.empty {
  padding: 2rem 1rem;
  text-align: center;
  color: #9ca3af;
  font-size: 0.9rem;
  font-style: italic;
}

.loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 2rem 1rem;
  color: #6b7280;
  font-size: 0.9rem;
}

.inline-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid #e5e7eb;
  border-top: 2px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error {
  padding: 1rem;
  color: #b91c1c;
  font-size: 0.85rem;
  text-align: center;
}
</style>
